import type {
  CreateAudioPostPayload,
  CreateVideoPostPayload,
} from '@fourthwall/services/dist/MembershipsCreatorApiClient'

import type { FormValues } from '../types'
import {
  getAudioPostPayload,
  getCreateLiveStreamPostPayload,
  getMultipleImagePostPayload,
  getPollPostPayload,
  getTextPostPayload,
  getVideoEmbedPostPayload,
  getVideoPostPayload,
} from '../utils/forms'
import { useCreateAudioPost } from './useCreateAudioPost'
import { useCreateLiveStreamPost } from './useCreateLiveStreamPost'
import { useCreateMultipleImagePostMutation } from './useCreateMultipleImagePostMutation'
import { useCreatePollPostMutation } from './useCreatePollPostMutation'
import { useCreateTextPostMutation } from './useCreateTextPostMutation'
import { useCreateVideoEmbedPost } from './useCreateVideoEmbedPost'
import { useCreateVideoPost } from './useCreateVideoPost'

export const usePostCreate = () => {
  const { createTextPostMutation } = useCreateTextPostMutation()
  const { createPollPostMutation } = useCreatePollPostMutation()
  const { createMultipleImagePostMutation } = useCreateMultipleImagePostMutation()
  const {
    createVideoPost,
    mediaUploadProgress: videoUploadProgress,
    isLoading: isVideoLoading,
  } = useCreateVideoPost()
  const { createVideoEmbedPost, isLoading: isVideoEmbedLoading } = useCreateVideoEmbedPost()
  const {
    createAudioPost,
    mediaUploadProgress: audioUploadProgress,
    isLoading: isAudioLoading,
  } = useCreateAudioPost()
  const { createLiveStreamPost, isLoading: isLiveStreamLoading } = useCreateLiveStreamPost()

  const createPost = async (values: FormValues, handleSuccess: () => void) => {
    if (values.postType === 'Text') {
      await createTextPostMutation.mutateAsync([getTextPostPayload(values)], {
        onSuccess: handleSuccess,
      })
    }

    if (values.postType === 'Poll') {
      await createPollPostMutation.mutateAsync([getPollPostPayload(values)], {
        onSuccess: handleSuccess,
      })
    }

    if (values.postType === 'Image') {
      await createMultipleImagePostMutation.mutateAsync([getMultipleImagePostPayload(values)], {
        onSuccess: handleSuccess,
      })
    }

    if (values.postType === 'LiveStream') {
      await createLiveStreamPost(
        values.videoThumbnail as File,
        getCreateLiveStreamPostPayload(values),
        handleSuccess,
      )
    }

    if (values.postType === 'Video') {
      if (values.videoType === 'upload') {
        const payload = await getVideoPostPayload(values)
        const subtitles =
          typeof values.videoSubtitles === 'object' ? values.videoSubtitles : undefined

        await createVideoPost(
          values.video as File,
          values.videoThumbnail as File,
          subtitles,
          payload as CreateVideoPostPayload,
          handleSuccess,
        )
      }

      if (values.videoType === 'embed') {
        const payload = await getVideoEmbedPostPayload(values)

        await createVideoEmbedPost(values.videoThumbnail as File, payload, handleSuccess)
      }
    }

    if (values.postType === 'Audio') {
      const payload = await getAudioPostPayload(values)

      await createAudioPost(
        values.audio as File,
        values.audioThumbnail as File,
        payload as CreateAudioPostPayload,
        handleSuccess,
      )
    }
  }

  return {
    createPost,
    mediaUploadProgress: videoUploadProgress || audioUploadProgress,
    isLoading:
      createTextPostMutation.isLoading ||
      createPollPostMutation.isLoading ||
      createMultipleImagePostMutation.isLoading ||
      isLiveStreamLoading ||
      isVideoLoading ||
      isVideoEmbedLoading ||
      isAudioLoading,
  }
}
